import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/rootReducers';
import { setDashboard } from '../../store/slices/DashboardSlice';
import { request } from '../../Utils/request';
import IntroSlider from '../../Components/IntroSlider';

const IntroSliderPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const banners = useSelector((state: RootState) => state.dashboard.introSliders);
  const location = useSelector((state: RootState) => state.location.location);
  
  // Memoize location coordinates to prevent unnecessary API calls
  const locationLat = useMemo(() => location?.lat, [location?.lat]);
  const locationLng = useMemo(() => location?.lng, [location?.lng]);
  
  // Track the last API call coordinates to prevent duplicate calls
  const lastApiCallRef = useRef<{ lat: number; lng: number } | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  useEffect(() => {
    const fetchLanding = async () => {
      // Skip if already fetching
      if (isFetchingRef.current) return;
      
      let endpoint = '/landing';
      let shouldFetch = false;
      
      // Add location parameters if available
      if (locationLat && locationLng) {
        // Check if coordinates have meaningfully changed (avoid calls for tiny coordinate changes)
        const lastCall = lastApiCallRef.current;
        const hasChanged = !lastCall || 
          Math.abs(lastCall.lat - locationLat) > 0.0001 || 
          Math.abs(lastCall.lng - locationLng) > 0.0001;
        
        if (hasChanged) {
          const queryParams = new URLSearchParams({
            latitude: locationLat.toString(),
            longitude: locationLng.toString(),
          });
          endpoint = `${endpoint}?${queryParams.toString()}`;
          shouldFetch = true;
          lastApiCallRef.current = { lat: locationLat, lng: locationLng };
        }
      } else {
        // Fetch without location if location is not available (only once)
        shouldFetch = !lastApiCallRef.current;
        if (shouldFetch) {
          lastApiCallRef.current = { lat: 0, lng: 0 }; // Mark as called
        }
      }
      
      if (shouldFetch) {
        isFetchingRef.current = true;
        try {
          const res = await request(endpoint, {
            method: 'GET',
          }) as any;
          console.log("landing", res.data);
          dispatch(setDashboard({
            banners: res.data.banners,
            introSliders: res.data.introSliders,
            categories: res.data.postCategories,
            newPlaces: res.data.newPlaces,
            curatedPlaces: res.data.curatedPlaces,
            tripsItinerary: res.data.tripsItinerary,
          }));
        } catch (error) {
          console.error('Error fetching landing data:', error);
        } finally {
          isFetchingRef.current = false;
        }
      }
    };
    
    fetchLanding();
  }, [locationLat, locationLng, dispatch]);

  const handleIntroComplete = () => {
    localStorage.setItem('hasSeenIntroSlider', 'true');
    navigate('/');
  };

  const handleIntroSkip = () => {
    localStorage.setItem('hasSeenIntroSlider', 'true');
    navigate('/');
  };

  // Show loading or redirect if no banners
  if (!banners || banners.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <IntroSlider
      slides={banners}
      onComplete={handleIntroComplete}
      onSkip={handleIntroSkip}
    />
  );
};

export default IntroSliderPage;
